import {
  FieldErrors,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';

import {
  IncomeDetailsFormT,
  monthlyPaymentDayOptions,
  MonthlyPaymentDayValueT,
  PAYMENT_DAY_OPTIONS,
  PAYMENT_WEEK_OPTIONS,
  paymentDayOptions,
  PaymentDayValueT,
  paymentScheduleOptions,
  PaymentScheduleValueT,
  paymentWeekOptions,
  PaymentWeekValueT,
} from '@consumer-frontend/nc-utils';

import { NcSelect } from '../NcSelect';

export type NcIncomeDetailsMonthlyFieldsPropsT = {
  errors: FieldErrors<IncomeDetailsFormT>;
  register: UseFormRegister<IncomeDetailsFormT>;
  watch: UseFormWatch<IncomeDetailsFormT>;
};

export const NcIncomeDetailsMonthlyFields = ({
  errors,
  register,
  watch,
}: NcIncomeDetailsMonthlyFieldsPropsT): React.ReactElement => {
  const selectedPaymentSchedule = watch('paymentSchedule');
  const selectedPaymentWeek = watch('paymentWeek');
  const selectedMonthlyWeekDay = watch('monthlyWeekDay');

  const showMonthlyPaymentDay = selectedPaymentSchedule === 'same_day_each_month';
  const showPaymentWeek = selectedPaymentSchedule === 'same_day_particular_week';
  const showMonthlyWeekDay = showPaymentWeek && !!selectedPaymentWeek;
  const showPaymentConfirmation = showMonthlyWeekDay && !!selectedMonthlyWeekDay;

  const paymentScheduleValidations = {
    shouldUnregister: true,
    validate: (option?: PaymentScheduleValueT) => {
      return paymentScheduleOptions.some((schedule) => schedule.value === option);
    },
  };

  const monthlyPaymentDayValidations = {
    shouldUnregister: true,
    validate: (option?: MonthlyPaymentDayValueT) => {
      return monthlyPaymentDayOptions.some((day) => day.value === option);
    },
  };

  const paymentWeekValidations = {
    shouldUnregister: true,
    validate: (option?: PaymentWeekValueT) => {
      return paymentWeekOptions.some((week) => week.value === option);
    },
  };

  const monthlyWeekDayValidations = {
    shouldUnregister: true,
    validate: (option?: PaymentDayValueT) => {
      return paymentDayOptions.some((day) => day.value === option);
    },
  };

  return (
    <>
      <NcSelect
        {...register('paymentSchedule', paymentScheduleValidations)}
        errorMessage={errors.paymentSchedule && 'Select your payment schedule.'}
        id="payment-schedule"
        label={<span className="form-label">Payment Schedule</span>}
        options={paymentScheduleOptions}
        placeholder="Select payment schedule"
      />
      {showMonthlyPaymentDay && (
        <NcSelect
          {...register('monthlyPaymentDay', monthlyPaymentDayValidations)}
          errorMessage={
            errors.monthlyPaymentDay && 'Select the day of the month you get paid.'
          }
          id="monthly-payment-day"
          label={<span className="form-label">Payment Day</span>}
          options={monthlyPaymentDayOptions}
          placeholder="Select day"
        />
      )}
      {showPaymentWeek && (
        <NcSelect
          {...register('paymentWeek', paymentWeekValidations)}
          errorMessage={
            errors.paymentWeek && 'Select the week of the month you get paid.'
          }
          id="payment-week"
          label={<span className="form-label">Payment Week</span>}
          options={paymentWeekOptions}
          placeholder="Select week"
        />
      )}
      {showMonthlyWeekDay && (
        <NcSelect
          {...register('monthlyWeekDay', monthlyWeekDayValidations)}
          errorMessage={
            errors.monthlyWeekDay && 'Select the day of the week you get paid.'
          }
          id="monthly-week-day"
          label={<span className="form-label">Payment Day</span>}
          options={paymentDayOptions}
          placeholder="Select day"
        />
      )}
      {showPaymentConfirmation && (
        <p>
          Based on your selections, you get paid on the{' '}
          <strong>
            {PAYMENT_WEEK_OPTIONS[selectedPaymentWeek].toLowerCase()}{' '}
            {PAYMENT_DAY_OPTIONS[selectedMonthlyWeekDay]} of each month.
          </strong>
        </p>
      )}
    </>
  );
};
